import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { CivSelector } from '../../components/CivSelector';
import { MapSelector } from '../../components/MapSelector';
import { mergeCivDraftView, mergeMapDraftView } from '../../helpers/mergeDraftView';
import { useSocketState } from '../../helpers/useSocket';
import Countdown from 'react-countdown';
import { countdownRenderer } from '../../helpers/countdownRenderer';
import { useQuery } from "react-query";

function DraftDashboard() {
    const { socket } = useOutletContext()
    const [match] = useSocketState('Match');
    const [gameCivDraft] = useSocketState('GameCivDraft');
    const [civDraft] = useSocketState('CivDraft');
    const [mapDraft] = useSocketState('MapDraft');
    const [gameCivDraftUrl, setGameCivDraftUrl] = useState(null);
    const [civDraftUrl, setCivDraftUrl] = useState(null);
    const [mapDraftUrl, setMapDraftUrl] = useState(null);

    // Delay load draftId
    if (gameCivDraftUrl === null && gameCivDraft?.draftId) {
        setGameCivDraftUrl(gameCivDraft.draftId);
    }
    if (civDraftUrl === null && civDraft?.draftId) {
        setCivDraftUrl(civDraft.draftId);
    }
    if (mapDraftUrl === null && mapDraft?.draftId) {
        setMapDraftUrl(mapDraft.draftId);
    }

    const fetchMaps = async () => {
        const res = await fetch(`${window.__RUNTIME_CONFIG__.REACT_APP_BACKEND_URL}/api/config/maps`);
        return res.json();
    };

    const {data: maps, status} = useQuery("maps", fetchMaps);

    const gameCivDraftView = gameCivDraft ? mergeCivDraftView(gameCivDraft?.view || {}, [], 0, false) : {};
    const civDraftView = civDraft && match ? mergeCivDraftView(civDraft?.view || {}, match.games, match.matchRedraftAfter, true) : {};
    const mapDraftView = mapDraft && match ? mergeMapDraftView(mapDraft?.view || {}, match.games, 0, true) : {};

    const gameOptions = ['Bo1', 'Bo3', 'Bo5', 'Bo7', 'Bo9', 'Bo11', 'Pa2', 'Pa3', 'Pa5', 'Pa7', 'Bo3 - ABBA', 'Bo5 - ABBA', 'Bo7 - ABBA', 'Bo1 - 2v2', 'Bo3 - 2v2', 'Bo5 - 2v2', 'Bo7 - 2v2']
    const gameStateOptions = ['left', 'right', 'upcoming', 'tbd', 'picked']
    const statusPresets = [
		{ id: 'drafting', value: 'Players are drafting' }, 
		{ id: 'beginning', value: 'Game is about to begin...' }, 
		{ id: 'finished', value: 'Thank you for your support! ♥' }, 
		{ id: 'interview', value: 'Interview is about to begin...' }, 
		{ id: 'lobby', value: 'Lobby is getting ready' }, 
		{ id: 'hide', value: '' }]

    const expectedNumGames = parseInt(match?.matchType.match(/\d+/)?.at(0) || '0');
    const hasRedraft = expectedNumGames >= 7;

    function handleImportGameCivDraft() {
        const draftId = gameCivDraftUrl.match(/[a-zA-Z0-9]{5,}$/)?.at(0);
        socket.emit('setGameCivDraftId', draftId);
    }

    function handleImportCivDraft() {
        const draftId = civDraftUrl.match(/[a-zA-Z0-9]{5,}$/)?.at(0)
        socket.emit('setCivDraftId', draftId);
        if (draftId) {
            // Auto set redraft, just for this tourney
            if (hasRedraft) {
                const nextGameIndex = match.games.findIndex(v => v.state === 'tbd' || v.state === 'upcoming');
                if (nextGameIndex === 4) {
                    socket.emit('modifyMatch', { matchRedraftAfter: 4 });
                } else if (nextGameIndex === 0) {
                    socket.emit('modifyMatch', { matchRedraftAfter: 0 });
                }
            } else if (match.matchRedraftAfter !== 0) {
                socket.emit('modifyMatch', { matchRedraftAfter: 0 });
            }
        }
    }

	function handleChangeCiv(el, index, side) {
		console.log(el, index, side)

		let newDraftOption = {...civDraft.view[side][index]}
		newDraftOption.option = el

		let newDraft = [...civDraft.view[side]]

		newDraft[index] = {...newDraft[index], ...newDraftOption}

		socket.emit(`modifyCiv${side}Draft`, newDraft)
	}

	function handleChangeMap(el, index, side) {
		let newDraftOption = {...mapDraft.view[side][index]}
		newDraftOption.option = el

		let newDraft = [...mapDraft.view[side]]

		newDraft[index] = {...newDraft[index], ...newDraftOption}

		socket.emit(`modifyMap${side}Draft`, newDraft)
	}

    function handleImportMapDraft() {
        const draftId = mapDraftUrl.match(/[a-zA-Z0-9]{5,}$/)?.at(0);
        socket.emit('setMapDraftId', draftId);
    }

    function handleSwapGameCivDraftHost() {
        socket.emit('swapGameCivDraftPlayers');
    }

    function handleSwapCivDraftHost() {
        socket.emit('swapCivDraftPlayers');
    }

    function handleSwapMapDraftHost() {
        socket.emit('swapMapDraftPlayers');
    }

    function handleGameCivDraftUrlChange(el) {
        setGameCivDraftUrl(el.target.value);
    }

    function handleCivDraftUrlChange(el) {
        setCivDraftUrl(el.target.value);
    }

    function handleMapDraftUrlChange(el) {
        setMapDraftUrl(el.target.value);
    }

    function handleMatchTypeChange(el) {
        socket.emit('modifyMatch', { matchType: el.target.value });
    }

    function handleMatchRedraftAfterChange(el) {
        socket.emit('modifyMatch', { matchRedraftAfter: el.target.value });
    }

    function modifyGame(gameIndex, patch) {
		
        let newGames = [...match.games];
        newGames[gameIndex] = { ...newGames[gameIndex], ...patch };
        socket.emit('modifyMatch', { games: newGames });
    }

    function modifyGameDraft(gameIndex, patch) {
        let newGames = [...match.games];
        newGames[gameIndex] = { ...newGames[gameIndex], ...patch };
        socket.emit('modifyMatch', { games: newGames });
    }

    function getFutureTimestamp(seconds) {
        if (seconds === null) {
            socket.emit('modifyMatch', { countdownTimer: null })
            return
        }
        var date = new Date().getTime() + seconds * 1000;
		socket.emit('modifyMatch', { countdownTimer: null })
		
        socket.emit('modifyMatch', { countdownTimer: date })
    }

    function handleMatchStatusChange(el) {
        const text = statusPresets.find(item => item.id === el.target.value)
        socket.emit('modifyMatch', { matchStatus: text.value })
    }

    function handleRoundInfo(el) {
        el.preventDefault()
        socket.emit('modifyMatch', { roundInfo: el.target.roundInfo.value })
    }

    function copyNamesFromGameCivDraft() {
        socket.emit('modifyLeftPlayer', {name: gameCivDraftView.hostName});
        socket.emit('modifyRightPlayer', {name: gameCivDraftView.guestName});
    }

    function copyNamesFromCivDraft() {
        socket.emit('modifyLeftPlayer', {name: civDraftView.hostName});
        socket.emit('modifyRightPlayer', {name: civDraftView.guestName});
    }
    
    function copyNamesFromMapDraft() {
        socket.emit('modifyLeftPlayer', {name: mapDraftView.hostName});
        socket.emit('modifyRightPlayer', {name: mapDraftView.guestName});
    }

    function convertFromMapDraft() {
        let newGames = [...match.games];

        let pickedMaps = [...mapDraftView.host, ...mapDraftView.admin, ...mapDraftView.guest].filter(pickedMap => pickedMap.action === "pick")
        console.log(pickedMaps)
        pickedMaps.map((pickedMap, i) => {
            if(newGames[i]) newGames[i].map = pickedMap.option
        })
        socket.emit('modifyMatch', { games: newGames });
    }
	
	function setAllMapsPicked() {
		let newGames = [...match.games];

        // set any game with state 'tbd' to 'picked'
        newGames = newGames.map(game => game && game.state === 'tbd' ? { ...game, state: 'picked' } : game);

		socket.emit('modifyMatch', { games: newGames });
	}

	function copyToUpcoming() {
		let newGames = [...match.games];

		// find first upcoming game
		const upcomingIndex = newGames.findIndex(g => g && g.state === 'upcoming');
		if (upcomingIndex === -1) return; // nothing to copy to

		if (!gameCivDraftView || !gameCivDraftView.host || !gameCivDraftView.guest) return;

		const leftGameCivDraft = gameCivDraftView.host
			.filter(d => d.action !== 'snipe')
			.map(d => ({ civ: d.option, action: d.action, result: d.result }));
		const rightGameCivDraft = gameCivDraftView.guest
			.filter(d => d.action !== 'snipe')
			.map(d => ({ civ: d.option, action: d.action, result: d.result }));

        leftGameCivDraft.forEach(element => {
            if (element.result === 'sniped')
                element.action = 'sniped';
        });

        rightGameCivDraft.forEach(element => {
            if (element.result === 'sniped')
                element.action = 'sniped';
        });

        const leftCivs = leftGameCivDraft.filter(v => v.action === 'pick').map(v => v.civ);
        const rightCivs = rightGameCivDraft.filter(v => v.action === 'pick').map(v => v.civ);

		newGames[upcomingIndex] = { ...newGames[upcomingIndex], leftCivs, rightCivs, leftGameCivDraft, rightGameCivDraft };

		socket.emit('modifyMatch', { games: newGames });
	}

    return (
        <>
            <div className='flex flex-row pt-4'>
                {
                    match?.games &&
                    <div className='flex flex-col'>
                        <div className='flex flex-row'>
                            <form className='flex flex-col' onSubmit={handleRoundInfo}>
                                <label>Round Info</label>
                                <input type="text" id="roundInfo" name="roundInfo" className='w-56 mr-8' defaultValue={match.roundInfo} />
                                <button className='w-24 mt-1' type='submit'>Submit</button>
                            </form>
                            <div className='flex flex-col'>
                                <label>Status</label>
                                <input type="text" id="draftUrl" name="draftUrl" className='w-56 mr-8' value={match.matchStatus} />
                                <select value={statusPresets.find(item => item.value === match.matchStatus).id} onChange={handleMatchStatusChange} className='border-solid border-[0.5px] h-8 w-56'>{statusPresets.map(option => <option value={option.id}>{option.value}</option>)}</select>
                            </div>
                            <div className='flex flex-col'>
                                <label>Timer</label>
                                <input type="text" id="countdownTimer" name="countdownTimer" className='w-56 mr-8' value={match?.countdownTimer} />
                                {match.countdownTimer ?
                                    <Countdown date={match.countdownTimer} renderer={countdownRenderer} />
                                    :
                                    <>00:00</>
                                }
                                <div>
                                    <button onClick={() => getFutureTimestamp(0)} className='w-12 h-8 p-0 bg-green-600 hover:bg-green-800 m-1'>0 min</button>
                                    <button onClick={() => getFutureTimestamp(60)} className='w-12 h-8 p-0 bg-green-600 hover:bg-green-800 m-1'>1 min</button>
                                    <button onClick={() => getFutureTimestamp(120)} className='w-12 h-8 p-0 bg-green-600 hover:bg-green-800 m-1'>2 min</button>
                                    <button onClick={() => getFutureTimestamp(180)} className='w-12 h-8 p-0 bg-green-600 hover:bg-green-800 m-1'>3 min</button>
                                    <button onClick={() => getFutureTimestamp(240)} className='w-12 h-8 p-0 bg-green-600 hover:bg-green-800 m-1'>4 min</button>
                                    <button onClick={() => getFutureTimestamp(300)} className='w-12 h-8 p-0 bg-green-600 hover:bg-green-800 m-1'>5 min</button>
                                    <button onClick={() => getFutureTimestamp(null)} className='w-12 h-8 p-0 bg-green-600 hover:bg-green-800 m-1'>HIDE</button>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-row">
                            <div className='flex flex-col'>
                                <label>Map Draft</label>
                                <div className='flex flex-row'>
                                    <input type="text" id="mapDraftUrl" name="mapDraftUrl" className='w-56 mr-8' value={mapDraftUrl} onChange={handleMapDraftUrlChange} />
                                    <button className='h-8 my-auto mr-2' onClick={handleImportMapDraft}>Import/Connect</button>
                                    <button className='h-8 my-auto mr-4 w-28 text-xs' onClick={copyNamesFromMapDraft}>Copy Names</button>
                                </div>
                                <label>Civ Draft</label>
                                <div className='flex flex-row'>
                                    <input type="text" id="civDraftUrl" name="civDraftUrl" className='w-56 mr-8' value={civDraftUrl} onChange={handleCivDraftUrlChange} />
                                    <button className='h-8 my-auto mr-2 ' onClick={handleImportCivDraft}>Import/Connect</button>
                                    <button className='h-8 my-auto mr-4 w-28 text-xs' onClick={copyNamesFromCivDraft}>Copy Names</button>
                                </div>
                                <label>Game Civ Draft</label>
                                <div className='flex flex-row'>
                                    <input type="text" id="gameCivDraftUrl" name="gameCivDraftUrl" className='w-56 mr-8' value={gameCivDraftUrl} onChange={handleGameCivDraftUrlChange} />
                                    <button className='h-8 my-auto mr-2 ' onClick={handleImportGameCivDraft}>Import/Connect</button>
                                    <button className='h-8 my-auto mr-4 w-28 text-xs' onClick={copyNamesFromGameCivDraft}>Copy Names</button>
                                </div>
                            </div>
                            <div className='flex flex-col'>
                            </div>
                            <div className='flex flex-col'>
                                <label>Format</label>
                                <select value={match.matchType} className='border-solid border-[0.5px] h-8 w-28' onChange={handleMatchTypeChange}>{gameOptions.map(option => <option value={option}>{option}</option>)}</select>
                            </div>
                            {
                                (hasRedraft || !!match.matchRedraftAfter) &&
                                <div className='flex flex-col'>
                                    <label>Redraft Offset</label>
                                    <input type="number" id="matchRedraftAfter" name="matchRedraftAfter" value={match.matchRedraftAfter || 0} className='border-solid border-[0.5px] h-8 w-16' onChange={handleMatchRedraftAfterChange} />
                                </div>
                            }
                        </div>
                        {gameCivDraftView?.host &&
                            <div className="mt-8 w-[600px]">
								<button className='h-6 py-0 my-auto float-right' onClick={copyToUpcoming}>Copy to Upcoming</button>
                                <h3 className="ml-2 mb-1 font-bold">Game Civ Draft:</h3>
                                <div className="flex flex-row">
                                    <div className='px-2 border-black' style={{ borderRightWidth: "3px" }}>
                                        <div className='pb-2'>
                                            Name: {gameCivDraftView.hostName}
                                        	<button className='h-6 py-0 my-auto float-right' onClick={handleSwapGameCivDraftHost}>Swap</button>
                                        </div>
                                        {gameCivDraftView.host.map(draftOption => <CivSelector civ={draftOption.option} state={draftOption.action} leftSide={true} allowEmpty={true} sniped={draftOption.result === 'sniped'}/>)}
                                        {/* <div className='flex flex-row justify-center'>
                                        <button className='bg-green-500'>+</button>
                                        </div> */}
                                    </div>

                                    <div className='px-2'>
                                        <div className='pb-2'>Name: {gameCivDraftView.guestName}</div>
                                        {gameCivDraftView.guest.map(draftOption => <CivSelector civ={draftOption.option} state={draftOption.action} leftSide={false} allowEmpty={true} sniped={draftOption.result === 'sniped'}/>)}
                                    </div>
                                </div>
                            </div>
                        }
                        {civDraftView?.host &&
                            <div className="mt-8">
                                <h3 className="ml-2 mb-1 font-bold">Match Civ Draft:</h3>
                                <div className="flex flex-row">
                                    <div className='px-2 border-black' style={{ borderRightWidth: "3px" }}>
                                        <div className='pb-2'>
                                            Name: {civDraftView.hostName}

                                            <button className='h-6 py-0 my-auto float-right' onClick={handleSwapCivDraftHost}>Swap</button>
                                        </div>
                                        {civDraftView.host.map((draftOption, i) => <CivSelector civ={draftOption.option} state={draftOption.action} leftSide={true} allowEmpty={true} index={i} onChange={handleChangeCiv} sniped={draftOption.result === 'sniped'}/>)}
                                        {/* <div className='flex flex-row justify-center'>
                                        <button className='bg-green-500'>+</button>
                                        </div> */}
                                    </div>

                                    <div className='px-2'>
                                        <div className='pb-2'>Name: {civDraftView.guestName}</div>
                                        {civDraftView.guest.map((draftOption, i) => <CivSelector civ={draftOption.option} state={draftOption.action} leftSide={false} allowEmpty={true} index={i} onChange={handleChangeCiv} sniped={draftOption.result === 'sniped'}/>)}
                                    </div>
                                </div>
                            </div>
                        }
                        {mapDraftView?.host &&
                            <div className="mt-8">
                                <h3 className="ml-2 mb-1 font-bold">Match Map Draft:</h3>
                                <div className="flex flex-row">
                                    <div className='px-2 border-black' style={{ borderRightWidth: "3px" }}>
                                        <div className='pb-2'>
                                            Name: {mapDraftView.hostName}

                                            <button className='h-6 py-0 my-auto float-right' onClick={handleSwapMapDraftHost}>Swap</button>
                                        </div>
                                        {mapDraftView.host.map((draftOption, index) => <MapSelector map={draftOption.option} state={draftOption.action} leftSide={true} allowEmpty={true} maps={maps} iconMode='small' onChange={handleChangeMap} index={index}/>)}
                                        {/* <div className='flex flex-row justify-center'>
                                        <button className='bg-green-500'>+</button>
                                        </div> */}
                                    </div>

                                    <div className='px-2'>
                                        <div className='pb-2'>Name: {mapDraftView.guestName}</div>
                                        {mapDraftView.guest.map((draftOption, index) => <MapSelector map={draftOption.option} state={draftOption.action} leftSide={false} allowEmpty={true} maps={maps} iconMode='small' onChange={handleChangeMap} index={index}/>)}
                                    </div>
                                </div>
                            </div>
                        }
                    </div>
                }

                {
                    match &&
                    <div className='flex flex-col mt-20 ml-8'>
						<div className='flex flex-row'>
                        	<button className='h-6 py-0 mx-auto w-56' onClick={convertFromMapDraft}>Convert from Map Draft</button>
                        	<button className='h-6 py-0 mx-auto w-56' onClick={setAllMapsPicked}>Set all to picked</button>
						</div>
                        {match?.games.map((game, gameIndex) =>
                            <div className='flex flex-row pt-4'>
                                <div className='flex flex-col'>
                                    {match.matchType.includes("2v2") ? <>
                                        <CivSelector key={`${gameIndex}0`} civ={game.leftCivs[0]} leftSide={true} allowEmpty={true} onChange={civ => modifyGame(gameIndex, { leftCivs: [civ, game.leftCivs[1]] })} />
                                        <CivSelector key={`${gameIndex}1`} civ={game.leftCivs[1]} leftSide={true} allowEmpty={true} onChange={civ => modifyGame(gameIndex, { leftCivs: [game.leftCivs[0], civ] })} />
                                    </> :
                                    match.matchType.includes("ABBA") ? <>
                                        <CivSelector key={`${gameIndex}0`} civ={game?.leftGameCivDraft?.[0]?.civ} state={game?.leftGameCivDraft?.[0]?.action} leftSide={true} allowEmpty={true} index={0} showDraftOptions sniped={game?.leftGameCivDraft?.[0]?.result === 'sniped'}/>
                                        <CivSelector key={`${gameIndex}1`} civ={game?.leftGameCivDraft?.[1]?.civ} state={game?.leftGameCivDraft?.[1]?.action} leftSide={true} allowEmpty={true} index={1} showDraftOptions sniped={game?.leftGameCivDraft?.[1]?.result === 'sniped'}/>
                                    </> :
                                        <CivSelector civ={game.leftCivs[0]} leftSide={true} allowEmpty={true} onChange={civ => modifyGame(gameIndex, { leftCivs: [civ] })} />
                                    }

                                </div>
                                <div className='flex flex-col'>
                                    <MapSelector map={game.map} allowEmpty={true} onChange={map => modifyGame(gameIndex, { map: map })} maps={maps}/>
                                    <select value={game.state} className='border-solid border-[0.5px] h-8 w-24 mx-auto' onChange={el => modifyGame(gameIndex, { state: el.target.value })}>
                                        {gameStateOptions.map(option => <option value={option}>{option}</option>)}
                                    </select>
                                </div>
                                <div className='flex flex-col'>
                                    {match.matchType.includes("2v2") ? <>
                                        <CivSelector civ={game.rightCivs[0]} leftSide={false} allowEmpty={true} onChange={civ => modifyGame(gameIndex, { rightCivs: [civ, game.rightCivs[1]] })} />
                                        <CivSelector civ={game.rightCivs[1]} leftSide={false} allowEmpty={true} onChange={civ => modifyGame(gameIndex, { rightCivs: [game.rightCivs[0], civ] })} />
                                    </> :
									match.matchType.includes("ABBA") ? <>
                                        <CivSelector civ={game?.rightGameCivDraft?.[0]?.civ} state={game?.rightGameCivDraft?.[0]?.action} leftSide={false} allowEmpty={true} index={0} showDraftOptions sniped={game?.rightGameCivDraft?.[0]?.result === 'sniped'}/>
                                        <CivSelector civ={game?.rightGameCivDraft?.[1]?.civ} state={game?.rightGameCivDraft?.[1]?.action} leftSide={false} allowEmpty={true} index={1} showDraftOptions sniped={game?.rightGameCivDraft?.[1]?.result === 'sniped'}/>
                                    </> :
                                        <CivSelector civ={game.rightCivs[0]} leftSide={false} allowEmpty={true} onChange={civ => modifyGame(gameIndex, { rightCivs: [civ] })} />
                                    }
                                </div>
                            </div>
                        )}
                        {/* <div className='flex flex-row justify-center pt-4'>
                            <button className='bg-green-500 hover:bg-green-700'>+</button>
                        </div> */}
                    </div>
                }
            </div >
        </>
    )
}

export default DraftDashboard;
